import * as React from 'react';
import {
  root as rootCls,
  fluid as fluidCls,
  header as headerCls,
  headerRow as headerRowCls,
  headerTitle as headerTitleCls,
  headerCaption as headerCaptionCls,
  filterRow as filterRowCls,
  messageList as messageListCls,
  attentionTraySlot as attentionTraySlotCls,
  composerSlot as composerSlotCls,
} from './ChatPanel.css';
import { TypingIndicator } from '../../feedback/TypingIndicator/TypingIndicator';

export interface ChatPanelProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /**
   * Override the entire header region with a single slot. When supplied,
   * `title` / `caption` / `headerAction` / `filters` are ignored and the
   * consumer is responsible for rendering the full header (typically a
   * `<WorkstreamHeader>`). Use this when the workstream header needs to
   * be richer than the default 3-row layout.
   */
  headerSlot?: React.ReactNode;
  /** Workstream name + decorations (cog, breadcrumb, focus pill). */
  title?: React.ReactNode;
  /** Caption beneath the title (e.g. "8 participants · 5 agents"). */
  caption?: React.ReactNode;
  /** Right-aligned slot in the header row (focus selector pill). */
  headerAction?: React.ReactNode;
  /** Filter row content (e.g. All / Team / My Actions pills). */
  filters?: React.ReactNode;
  /** Message stream — typically a list of `MessageBubble`s. */
  messages: React.ReactNode;
  /**
   * When true, renders a `<TypingIndicator>` as the LAST item of the
   * message list (the "…is typing" lozenge) — shown while the chat awaits
   * a reply. Rendered as the final list child so the panel's auto-scroll
   * pins to it and the `role="status"` live region announces on appear.
   */
  awaitingReply?: boolean;
  /**
   * Accessible label for the typing indicator (when `awaitingReply`).
   * Defaults to the `TypingIndicator` default ("Workstream Agent is
   * typing").
   */
  typingLabel?: string;
  /** Attention tray ("NEEDS YOUR ATTENTION 3" + cards). */
  attention?: React.ReactNode;
  /** Composer (chat input + paperclip / lightning / send). */
  composer: React.ReactNode;
  /**
   * Fluid mode — drops the canonical `40%` width / `min-width: 360` /
   * left border so the panel fills its parent. Used by the compact
   * responsive tier where the chat panel is rendered as a single
   * full-pane view via a bottom tab, not as a sibling column to an
   * artifact workspace.
   */
  fluid?: boolean;
  /** ref-as-prop. */
  ref?: React.Ref<HTMLElement>;
}

/**
 * ChatPanel — right-hand coordination column of the workspace shell.
 * Pure structural shell; the consumer supplies the content via slots.
 *
 * Prototype: `http://localhost:8000/` workspace right column.
 */
export function ChatPanel({
  headerSlot,
  title,
  caption,
  headerAction,
  filters,
  messages,
  awaitingReply = false,
  typingLabel,
  attention,
  composer,
  fluid,
  ref,
  className,
  'aria-label': ariaLabel = 'Workstream conversation',
  ...rest
}: ChatPanelProps) {
  const cls = [rootCls, fluid && fluidCls, className].filter(Boolean).join(' ');
  return (
    <section ref={ref} className={cls} aria-label={ariaLabel} {...rest}>
      {headerSlot ? (
        headerSlot
      ) : (
        <>
          {/* Render the header band only when it has content — an empty band
              (no title / caption / action) would otherwise show as a stray
              white strip with a divider above the message list. */}
          {(title || caption || headerAction) && (
            <div className={headerCls}>
              <div className={headerRowCls}>
                <div className={headerTitleCls}>{title}</div>
                {headerAction}
              </div>
              {caption ? <div className={headerCaptionCls}>{caption}</div> : null}
            </div>
          )}
          {filters ? <div className={filterRowCls}>{filters}</div> : null}
        </>
      )}

      <div className={messageListCls}>
        {messages}
        {awaitingReply ? <TypingIndicator label={typingLabel} /> : null}
      </div>

      {attention ? <div className={attentionTraySlotCls}>{attention}</div> : null}

      <div className={composerSlotCls}>{composer}</div>
    </section>
  );
}
ChatPanel.displayName = 'ChatPanel';
